import React, { FC, ReactNode } from 'react';

import { Box, Stack, Typography } from '@mui/material';

import { primaryColors, vestaGradient } from '~components/theme/styles/colors';

type HighlightType = 'completed' | 'current' | 'upcoming';

export const HorizontalRoadmapStep: FC<{ current?: boolean }> = ({ current }) => (
  <Stack spacing={2} direction={'row'} alignItems={'center'}>
    <Box
      sx={{
        background: current ? vestaGradient : primaryColors[900],
        borderRadius: '64px',
        minWidth: 16,
        width: 16,
        height: 16,
      }}
    />
    <Box
      sx={{
        background: current ? vestaGradient : primaryColors[900],
        height: 4,
        width: '100%',
        borderRadius: '8px',
      }}
    />
  </Stack>
);

const HighlightablePanel: FC<{ panelTitle: string; highlight: HighlightType; children?: ReactNode }> = ({
  panelTitle,
  children,
  highlight,
}) => {
  function decorationColor() {
    switch (highlight) {
      case 'completed':
        return [primaryColors[700], primaryColors[700]];
      case 'current':
        return [vestaGradient, vestaGradient];
      case 'upcoming':
        return [vestaGradient, primaryColors[700]];
    }
  }

  const decorationBg = decorationColor();

  return (
    <Stack direction={'column'} spacing={2}>
      <Stack direction={'row'} alignItems={'center'} spacing={4}>
        <Box
          sx={{ background: decorationBg[0], borderRadius: '64px', minWidth: 16, width: 16, height: 16 }}
        />
        <Typography variant={'label2'} color={primaryColors[400]}>
          {panelTitle}
        </Typography>
      </Stack>
      <Stack direction={'row'} pl={0.75} spacing={4}>
        <Box
          sx={{ background: decorationBg[1], minWidth: 4, maxWidth: 4, width: '100%', borderRadius: '8px' }}
        />
        <Stack spacing={2}>{children}</Stack>
      </Stack>
    </Stack>
  );
};

export const VerticalRoadmapPanel: FC<{
  stepTitle: string;
  highlight: HighlightType;
  subtitle?: string;
  subtitleVariant?: any;
  children?: ReactNode;
}> = ({ stepTitle, subtitle, subtitleVariant, children, highlight }) => {
  return (
    <HighlightablePanel highlight={highlight} panelTitle={stepTitle}>
      {subtitle ? (
        <Typography variant={subtitleVariant ? subtitleVariant : 'h4'}>{subtitle}</Typography>
      ) : null}
      <Typography width={{ xs: '100%', sm: '60%' }} variant={'body1'} color={primaryColors[400]}>
        {children}
      </Typography>
    </HighlightablePanel>
  );
};

export const ProcessPanel: FC<{
  stepTitle: string;
  highlight: HighlightType;
  description: string;
  duration?: number | string;
  children?: ReactNode;
}> = ({ stepTitle, description, duration, children, highlight }) => {
  return (
    <HighlightablePanel highlight={highlight} panelTitle={stepTitle}>
      <Typography variant={'h6'}>{description}</Typography>
      <Typography variant={'body2'} color={primaryColors[400]}>
        {' '}
        Duration: {typeof duration === 'string' ? duration : `~${duration} days`}
      </Typography>
      {children}
    </HighlightablePanel>
  );
};

export const RoadmapPanel: FC<{ current?: boolean; quarterText: string; quarterDescription: string }> = ({
  current,
  quarterDescription,
  quarterText,
}) => (
  <Stack spacing={2}>
    <HorizontalRoadmapStep current={current} />
    <Typography variant={'label1'} color={primaryColors[400]}>
      {quarterText}
    </Typography>
    <Typography variant={'body2'} color={primaryColors[100]}>
      {quarterDescription}
    </Typography>
  </Stack>
);
